import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Work,
  LocationOn,
  Schedule,
  AttachMoney,
  CheckCircle,
  CloudUpload,
  People,
  TrendingUp,
  School,
  Favorite
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import currentOpeningsData from '../data/careers/currentOpeningsData.json';
import whyWorkWithUsData from '../data/careers/whyWorkWithUsData.json';

const Careers = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    coverLetter: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const whyWorkWithUsDataIconMap = {
    AttachMoney: <AttachMoney sx={{ fontSize: 40 }} />,
    TrendingUp: <TrendingUp sx={{ fontSize: 40 }} />,
    Schedule: <Schedule sx={{ fontSize: 40 }} />,
    Favorite: <Favorite sx={{ fontSize: 40 }} />,
    School: <School sx={{ fontSize: 40 }} />,
    People: <People sx={{ fontSize: 40 }} />,
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedJob(null);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setShowSuccess(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      experience: '',
      coverLetter: '',
    });
    setTimeout(() => setShowSuccess(false), 5000);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '2.5rem', md: '3rem' },
              }}
            >
              Join Our Team
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 300,
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                mb: 4,
              }}
            >
              Build Your Career with VinPriv Talent Solutions
            </Typography>
            <Typography variant="h6" sx={{ fontStyle: 'italic' }}>
              "Where Talent Meets Opportunity"
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Open Positions */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            color: 'text.primary',
            fontWeight: 'bold',
            mb: 6,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '4px',
              backgroundColor: 'primary.main',
              borderRadius: '2px',
            },
          }}
        >
          Current Openings
        </Typography>

        {/* Scrollable Grid */}
        <Box
          sx={{
            maxHeight: '500px',
            overflowY: 'auto',
            // pr: 1,
            scrollbarWidth: 'thin',
            backgroundColor: '#f5f5f5', // Or use #b0a8b9 for contrast
            borderRadius: 2,
            px: 2,
            py: 2,
            gap: 4,
            scrollbarColor: '#1976d2 #e0e0e0',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#1976d2',
              borderRadius: '4px',
            },
          }}
        >
          <Grid container spacing={4}>
            {currentOpeningsData.map((job, index) => (
              <Grid item xs={12} md={6} key={index} sx={{ display: 'flex' }}>
                <motion.div
                  style={{ flex: 1, display: 'flex' }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      height: '100%',
                      width: '100%',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                        {job.title}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip icon={<LocationOn />} label={job.location} size="small" color="primary" />
                        <Chip icon={<Work />} label={job.type} size="small" color="secondary" />
                        <Chip icon={<Schedule />} label={job.experience} size="small" />
                        <Chip icon={<AttachMoney />} label={job.salary} size="small" color="success" />
                      </Box>
                      <Typography variant="body1" paragraph>
                        {job.description}
                      </Typography>
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Key Skills:
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        {job.skills.map((skill, skillIndex) => (
                          <Chip
                            key={skillIndex}
                            label={skill}
                            size="small"
                            sx={{ m: 0.25, bgcolor: 'grey.100' }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Button variant="contained" fullWidth onClick={() => handleJobClick(job)}>
                        View Details & Apply
                      </Button>
                    </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>


      {/* Benefits & Culture */}
      {/* <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 'bold' }}
          >
            Why Work With Us?
          </Typography>
          <Grid container spacing={4}>
            {whyWorkWithUsData.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      height: '100%',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: 70,
                        height: 70,
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      {whyWorkWithUsDataIconMap[benefit.icon]}
                    </Avatar>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {benefit.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
  <Container maxWidth="lg">
    <Typography
      variant="h3"
      component="h2"
      textAlign="center"
      gutterBottom
      sx={{ mb: 6, fontWeight: 'bold' }}
    >
      Why Work With Us?
    </Typography>
    <Grid container spacing={4}>
      {whyWorkWithUsData.map((benefit, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
          >
            <Paper
              elevation={2}
              sx={{
                p: 3,
                height: '300px', // Fixed height
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                },
              }}
            >
              <Box>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 70,
                    height: 70,
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {whyWorkWithUsDataIconMap[benefit.icon]}
                </Avatar>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {benefit.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {benefit.description}
                </Typography>
              </Box>
            </Paper>
          </motion.div>
        </Grid>
      ))}
    </Grid>
  </Container>
</Box>


      {/* Resume Upload Form */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{ mb: 6, fontWeight: 'bold' }}
        >
          Don't See a Perfect Match?
        </Typography>
        <Typography
          variant="h6"
          textAlign="center"
          color="text.secondary"
          sx={{ mb: 4 }}
        >
          Send us your resume and we'll keep you in mind for future opportunities!
        </Typography>

        {showSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Thank you for your application! We'll review your profile and get back to you soon.
          </Alert>
        )}

        <Paper elevation={2} sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Years of Experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Cover Letter / Additional Information"
                  name="coverLetter"
                  multiline
                  rows={4}
                  value={formData.coverLetter}
                  onChange={handleInputChange}
                  placeholder="Tell us about your experience, skills, and why you'd like to join VinPriv..."
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  sx={{ mb: 2 }}
                >
                  Upload Resume
                  <input type="file" hidden accept=".pdf,.doc,.docx" />
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Accepted formats: PDF, DOC, DOCX (Max size: 5MB)
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{ py: 1.5 }}
                >
                  Submit Application
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>

      {/* Job Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedJob && (
          <>
            <DialogTitle>
              <Typography variant="h4" fontWeight="bold">
                {selectedJob.title}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Chip
                  icon={<LocationOn />}
                  label={selectedJob.location}
                  size="small"
                  color="primary"
                />
                <Chip
                  icon={<Work />}
                  label={selectedJob.type}
                  size="small"
                  color="secondary"
                />
                <Chip
                  icon={<Schedule />}
                  label={selectedJob.experience}
                  size="small"
                />
                <Chip
                  icon={<AttachMoney />}
                  label={selectedJob.salary}
                  size="small"
                  color="success"
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                {selectedJob.description}
              </Typography>

              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
                Requirements:
              </Typography>
              <List>
                {selectedJob.requirements.map((req, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={req} />
                  </ListItem>
                ))}
              </List>

              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
                Key Skills:
              </Typography>
              <Box>
                {selectedJob.skills.map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill}
                    sx={{ m: 0.5, bgcolor: 'primary.main', color: 'white' }}
                  />
                ))}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={handleCloseDialog}>Close</Button>
              <Button variant="contained" size="large">
                Apply Now
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Careers;